import React, { useState, useEffect, useRef, useCallback } from 'react';
import './App.css';
import ChatInterface from './components/ChatInterface';
import Header from './components/Header';
import LeftPanel from './components/LeftPanel';
import WorkspaceHeader from './components/WorkspaceHeader';
import WorkspaceOptionsPanel from './components/WorkspaceOptionsPanel';
import WorkspaceSelectionModal from './components/WorkspaceSelectionModal';
import CloneWorkspaceModal from './components/CloneWorkspaceModal';
import SyncWorkspaceModal from './components/SyncWorkspaceModal';
import ShareWorkspaceModal from './components/ShareWorkspaceModal';
import GlossaryModal from './components/GlossaryModal';
import GenericConnectionDialog from './components/GenericConnectionDialog';
import AddConnectionModal from './components/AddConnectionModal';
import WorkspaceInstructionsModal from './components/WorkspaceInstructionsModal';
import LogsModal from './components/LogsModal';
import WorkspaceFilesModal from './components/WorkspaceFilesModal';
import ExploreDataSourcesModal from './components/ExploreDataSourcesModal';
import CreateOrganizationFlow from './components/CreateOrganizationFlow';
import WorkspaceLandingPage from './components/WorkspaceLandingPage';
import ApiKeyModal from './components/ApiKeyModal';
import ReportEditorPage from './components/ReportEditorPage';
import { WorkspaceProvider } from './context/WorkspaceContext';
import { ThemeProvider } from './context/ThemeContext';
import { SignIn, SignedIn, SignedOut, useUser, useOrganization, useClerk } from '@clerk/clerk-react';
import { getApiBaseUrl, getOrgSlug } from './config/backend.config';
import { AlertTriangle } from 'lucide-react';

function App() {
  const { user, isLoaded } = useUser(); // Get user from Clerk
  const { organization, isLoaded: orgLoaded } = useOrganization(); // Get organization
  const clerk = useClerk(); // Get Clerk instance for auth tokens
  const [isConnected, setIsConnected] = useState(false);
  const [selectedWorkspace, setSelectedWorkspace] = useState(null);
  const [versionStatus, setVersionStatus] = useState(null);
  const [savedReports, setSavedReports] = useState([]);
  const [templateModalOpen, setTemplateModalOpen] = useState(false);
  const [isSavingWorkspace, setIsSavingWorkspace] = useState(false);
  const [cloneModalOpen, setCloneModalOpen] = useState(false);
  const [cloneSourceWorkspace, setCloneSourceWorkspace] = useState(null);
  const [syncModalOpen, setSyncModalOpen] = useState(false);
  const [showGlossaryModal, setShowGlossaryModal] = useState(false);
  const [showAddConnectionModal, setShowAddConnectionModal] = useState(false);
  const [addConnectionCallback, setAddConnectionCallback] = useState(null);
  const [activeConnectionEdit, setActiveConnectionEdit] = useState(null); // { type, id, config, onSave }
  const [logsModalOpen, setLogsModalOpen] = useState(false);
  const [filesModalOpen, setFilesModalOpen] = useState(false);
  const [instructionsModalOpen, setInstructionsModalOpen] = useState(false);
  const [apiKeyModalOpen, setApiKeyModalOpen] = useState(false);
  const [shareConfirmOpen, setShareConfirmOpen] = useState(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(null);
  const [workspaceListVersion, setWorkspaceListVersion] = useState(0);
  const [currentModel, setCurrentModel] = useState('haiku');
  const [exploreModalOpen, setExploreModalOpen] = useState(false);
  const [exploreConnection, setExploreConnection] = useState(null);
  const [selectedReferenceFiles, setSelectedReferenceFiles] = useState([]);
  const [reportEditorPage, setReportEditorPage] = useState(null); // { reportId, reportName }
  const [workspaceRestoreAttempted, setWorkspaceRestoreAttempted] = useState(false);

  const [reportProgressEvents, setReportProgressEvents] = useState({}); // { [jobId]: { status, logLines: [], reportName } }

  const [headerHidden, setHeaderHidden] = useState(false);

  const chatInterfaceRef = useRef(null);
  const savedReportsPanelRef = useRef(null);
  const leftPanelRef = useRef(null);
  const versionCheckIntervalRef = useRef(null);
  const lastScrollTopRef = useRef(0);

  // Auto-save refs
  const autoSaveDebounceRef = useRef(null);
  const lastSaveTimestampMapRef = useRef({}); // { [workspace_id]: timestamp } — per-workspace cooldown
  const autoSaveInProgressRef = useRef(false);
  const selectedWorkspaceRef = useRef(null);
  const agentHasProcessedRef = useRef(false); // true once the agent has processed at least one request

  const handleLogout = () => {
    setIsConnected(false);
    setSelectedWorkspace(null);
    setTemplateModalOpen(false);
    setIsSavingWorkspace(false);
    setSyncModalOpen(false);
    setLogsModalOpen(false);
    setFilesModalOpen(false);
    setInstructionsModalOpen(false);
    setShareConfirmOpen(null);
    setDeleteConfirmOpen(null);
    setExploreModalOpen(false);
    // Clear saved workspace on logout so user can choose fresh on next login
    localStorage.removeItem('lastSelectedWorkspaceId');
    setWorkspaceRestoreAttempted(false);
  };

  const handleWorkspaceSelect = async (workspace) => {
    console.log('Workspace selected:', workspace);

    try {
      // Clear chat history when switching workspaces
      if (chatInterfaceRef.current) {
        chatInterfaceRef.current.clearMessages();
      }

      setSelectedWorkspace(workspace);
      setTemplateModalOpen(false);

      // Save selected workspace ID to localStorage for auto-restore on next login
      if (workspace?.workspace_id) {
        localStorage.setItem('lastSelectedWorkspaceId', workspace.workspace_id);
      }

      console.log('Workspace context:', {
        workspace_id: workspace.workspace_id,
        workspace_name: workspace.workspace_name
      });

      // NOTE: WebSocket connection will be established by ChatInterface component
      // when it detects selectedWorkspace is set

    } catch (error) {
      console.error('Error selecting workspace:', error);
      alert(`Failed to select workspace: ${error.message}`);
    }
  };

  const checkWorkspaceVersion = async (workspaceId) => {
    if (!workspaceId || !user || !organization) return;

    try {
      const token = await clerk.session?.getToken();
      if (!token) return;

      const response = await fetch(
        `${getApiBaseUrl()}/workspaces/${workspaceId}/version-status`,
        {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          console.log('Workspace version check: workspace not found');
        } else {
          console.error('Error checking version:', response.statusText);
        }
        return;
      }

      const status = await response.json();
      console.log('Version check result:', status);
      setVersionStatus(status);
    } catch (error) {
      console.error('Error checking workspace version:', error);
    }
  };

  const handleReportProgress = useCallback((event) => {
    const { job_id, event: eventType, status, log_line, success, report_name } = event;
    if (!job_id) return;

    setReportProgressEvents(prev => {
      const existing = prev[job_id] || { status: 'queued', logLines: [], reportName: report_name || '' };
      const updated = { ...existing };

      if (report_name) {
        updated.reportName = report_name;
      }

      if (eventType === 'status_change') {
        updated.status = status;
        if (success !== undefined) {
          updated.success = success;
        }
      } else if (eventType === 'log_line' && log_line) {
        updated.logLines = [...existing.logLines, log_line];
      }

      return { ...prev, [job_id]: updated };
    });

    // Auto-save when a report finishes generating successfully
    if (eventType === 'status_change' && status === 'complete' && success) {
      const workspace = selectedWorkspaceRef.current;
      if (workspace && !autoSaveInProgressRef.current) {
        autoSaveInProgressRef.current = true;
        handleSaveWorkspace(workspace)
          .then(() => {
            lastSaveTimestampMapRef.current[workspace.workspace_id] = Date.now();
            console.log('Auto-save after report generation completed for workspace', workspace.workspace_id);
          })
          .catch((error) => console.error('Auto-save after report generation failed:', error))
          .finally(() => { autoSaveInProgressRef.current = false; });
      }
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSaveReport = (reportData) => {
    // Trigger the SavedReportsPanel callback to open modal with save progress
    // Don't add to savedReports here - the in-progress state is managed by
    // handleSaveReportStarted, and the final report will be fetched from the API
    // when generation completes via loadSavedReports()
    if (savedReportsPanelRef.current?.onSaveReportStarted) {
      savedReportsPanelRef.current.onSaveReportStarted(reportData);
    }
  };

  const handleOpenSaveDialog = () => {
    if (chatInterfaceRef.current) {
      chatInterfaceRef.current.openSaveDialog();
    }
  };

  const handleLoadReport = (report) => {
    // This will be handled by the SavedReportsPanel component
    console.log('Loading report:', report);
  };

  const handleTemplateChange = () => {
    setSelectedWorkspace(null);
    setIsConnected(false);
  };

  const handleTemplateModalClose = () => {
    setTemplateModalOpen(false);
  };

  // Auto-save: triggered when agent transitions from processing to idle
  const handleProcessingChange = useCallback((isProcessing) => {
    // Cancel any pending auto-save if agent starts processing again
    if (isProcessing) {
      agentHasProcessedRef.current = true;
      if (autoSaveDebounceRef.current) {
        clearTimeout(autoSaveDebounceRef.current);
        autoSaveDebounceRef.current = null;
      }
      return;
    }

    // Don't auto-save on initial mount — only after the agent has actually processed a request
    if (!agentHasProcessedRef.current) return;

    // Agent just went idle — schedule an auto-save after a short debounce
    if (autoSaveDebounceRef.current) {
      clearTimeout(autoSaveDebounceRef.current);
    }

    autoSaveDebounceRef.current = setTimeout(async () => {
      // Use ref for workspace to avoid stale closure
      const workspace = selectedWorkspaceRef.current;
      if (!workspace) return;

      const wsId = workspace.workspace_id;
      const now = Date.now();
      const lastSave = lastSaveTimestampMapRef.current[wsId] || 0;
      const AUTO_SAVE_COOLDOWN = 30000; // 30 seconds

      if (now - lastSave < AUTO_SAVE_COOLDOWN) return;
      if (autoSaveInProgressRef.current) return;

      try {
        autoSaveInProgressRef.current = true;
        await handleSaveWorkspace(workspace);
        lastSaveTimestampMapRef.current[wsId] = Date.now();
        console.log('Auto-save completed for workspace', wsId);
      } catch (error) {
        console.error('Auto-save failed:', error);
      } finally {
        autoSaveInProgressRef.current = false;
      }
    }, 3000); // 3-second debounce after agent goes idle
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Manual save with timestamp tracking (so auto-save respects the cooldown)
  const handleSaveWorkspaceClick = async () => {
    if (!selectedWorkspace || isSavingWorkspace) return;
    try {
      setIsSavingWorkspace(true);
      await handleSaveWorkspace(selectedWorkspace);
      lastSaveTimestampMapRef.current[selectedWorkspace.workspace_id] = Date.now();
    } catch (error) {
      console.error('Save failed:', error);
    } finally {
      setIsSavingWorkspace(false);
    }
  };

  // Keep workspace ref in sync and clean up auto-save on workspace change
  useEffect(() => {
    selectedWorkspaceRef.current = selectedWorkspace;
    agentHasProcessedRef.current = false;
    return () => {
      if (autoSaveDebounceRef.current) {
        clearTimeout(autoSaveDebounceRef.current);
        autoSaveDebounceRef.current = null;
      }
    };
  }, [selectedWorkspace]);

  const handleCloneWorkspace = () => {
    setCloneSourceWorkspace(selectedWorkspace);
    setCloneModalOpen(true);
  };

  const handleCloneSharedWorkspace = (sharedWorkspace) => {
    setCloneSourceWorkspace(sharedWorkspace);
    setCloneModalOpen(true);
  };

  const handleCloneModalClose = (result) => {
    setCloneModalOpen(false);
    setCloneSourceWorkspace(null);

    // If clone succeeded, switch to the new workspace
    if (result?.success && result?._newWorkspace) {
      setSelectedWorkspace(result._newWorkspace);
      setIsConnected(false);
    }
  };

  const handleSyncWorkspace = () => {
    setSyncModalOpen(true);
  };

  const handleSyncModalClose = () => {
    setSyncModalOpen(false);
  };

  const handleToggleGlossary = () => {
    setShowGlossaryModal(prev => !prev);
  };

  const handleAddConnection = (reloadCallback) => {
    setAddConnectionCallback(() => reloadCallback);
    setShowAddConnectionModal(true);
  };

  const handleAddConnectionClose = () => {
    setShowAddConnectionModal(false);
    setAddConnectionCallback(null);
  };

  const handleConnectionTypeSelected = async (connectionType) => {
    setShowAddConnectionModal(false);

    // Generate a unique GUID for the new connection
    const connectionId = crypto.randomUUID();

    setActiveConnectionEdit({
      type: connectionType,
      id: connectionId,
      config: null,
      onSave: addConnectionCallback
    });
    setAddConnectionCallback(null);
  };

  const handleConnectionClick = (connectionType, connectionId, existingConfig, reloadCallback) => {
    setActiveConnectionEdit({
      type: connectionType,
      id: connectionId,
      config: existingConfig,
      onSave: reloadCallback
    });
  };

  const handleConnectionEditorClose = () => {
    setActiveConnectionEdit(null);
  };

  const handleConnectionEditorSave = (savedConnectionInfo) => {
    const wasNewConnection = activeConnectionEdit && !activeConnectionEdit.config;

    if (activeConnectionEdit?.onSave) {
      activeConnectionEdit.onSave();
    }
    setActiveConnectionEdit(null);

    // Auto-open explore dialog for newly added connections
    if (wasNewConnection && savedConnectionInfo) {
      handleExploreDataSources(savedConnectionInfo);
    }
  };

  const handleExploreDataSources = (connection = null) => {
    setExploreConnection(connection);
    setExploreModalOpen(true);
  };

  const handleExploreModalClose = () => {
    setExploreModalOpen(false);
    setExploreConnection(null);
  };

  const handleSendExploreCommand = (command) => {
    // Send the /explore_ds command through the chat interface
    if (chatInterfaceRef.current) {
      // We'll expose a sendExploreCommand method on ChatInterface
      chatInterfaceRef.current.sendExploreCommand(command);
    }
    setExploreModalOpen(false);
  };

  const handleInstructionsModalClose = () => {
    setInstructionsModalOpen(false);
  };

  const handleShareModalClose = () => {
    setShareConfirmOpen(null);
  };

  const handleDeleteModalClose = () => {
    setDeleteConfirmOpen(null);
  };

  const handleLogsModalClose = () => {
    setLogsModalOpen(false);
  };

  const handleShareWorkspace = async () => {
    if (!shareConfirmOpen?.workspace_id) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) throw new Error('Unable to get authentication token');

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      const isCurrentlyShared = shareConfirmOpen.is_shared === true;

      // Auto-save before sharing so non-owners get the latest content
      if (!isCurrentlyShared) {
        try {
          await handleSaveWorkspace(shareConfirmOpen);
        } catch (saveError) {
          console.error('Error saving workspace before share:', saveError);
          throw new Error('Failed to save workspace before sharing. Please try again.');
        }
      }

      const response = await fetch(`${apiBaseUrl}/workspaces/${shareConfirmOpen.workspace_id}/share`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          is_shared: !isCurrentlyShared,
          owner_name: user?.firstName || user?.username || 'User',
          owner_email: user?.primaryEmailAddress?.emailAddress || user?.emailAddresses?.[0]?.emailAddress || ''
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        let errorMsg = 'Failed to update workspace share status';

        if (typeof errorData === 'string') {
          errorMsg = errorData;
        } else if (Array.isArray(errorData.detail)) {
          // Handle validation errors array
          errorMsg = errorData.detail.map((err) => err.msg).join(', ');
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        }

        throw new Error(errorMsg);
      }

      // Refresh selected workspace to update share status
      setSelectedWorkspace(prev => ({
        ...prev,
        is_shared: !isCurrentlyShared
      }));
    } catch (error) {
      console.error('Error sharing workspace:', error);
      throw error;
    }
  };

  const handleDeleteWorkspace = async () => {
    if (!deleteConfirmOpen?.workspace_id) return;

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) throw new Error('Unable to get authentication token');

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);
      const workspaceId = deleteConfirmOpen.workspace_id;

      const response = await fetch(`${apiBaseUrl}/workspaces/${workspaceId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }));
        let errorMsg = 'Failed to delete workspace';

        if (typeof errorData === 'string') {
          errorMsg = errorData;
        } else if (Array.isArray(errorData.detail)) {
          // Handle validation errors array
          errorMsg = errorData.detail.map((err) => err.msg).join(', ');
        } else if (errorData.detail) {
          errorMsg = errorData.detail;
        }

        throw new Error(errorMsg);
      }

      // Clear selection and close dialog
      setSelectedWorkspace(null);
      setDeleteConfirmOpen(null);

      // Trigger workspace deleted handler and refresh landing page
      handleWorkspaceDeleted(workspaceId);
      setWorkspaceListVersion(v => v + 1);
    } catch (error) {
      console.error('Error deleting workspace:', error);
      const errorMsg = error instanceof Error ? error.message : 'Unknown error occurred';
      alert(`Error: ${errorMsg}`);
    }
  };

  const handleModelChange = async (newModel) => {
    setCurrentModel(newModel);
    // Update the workspace with the new model without clearing it
    // This will trigger ChatInterface to reconnect with the model parameter
    setSelectedWorkspace(prev => ({
      ...prev,
      model: newModel
    }));
  };

  const handleSaveWorkspace = async (workspace) => {
    if (!workspace?.workspace_id) {
      throw new Error('Workspace ID is required.');
    }

    if (!clerk?.session) {
      throw new Error('Authentication required. Please sign in again.');
    }

    try {
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Commit and push workspace to remote git repo
      const response = await fetch(`${apiBaseUrl}/workspaces/${workspace.workspace_id}/save`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to save workspace: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.success) {
        await checkWorkspaceVersion(workspace.workspace_id);
      }

      return result;
    } catch (error) {
      console.error('Error saving workspace:', error);
      throw error;
    }
  };

  const handleSync = async (workspace, forceSync = false) => {
    // workspace is the object passed from SyncWorkspaceModal containing workspace data
    if (!workspace?.workspace_id) {
      throw new Error('Workspace ID is required.');
    }

    if (!clerk?.session) {
      throw new Error('Authentication required. Please sign in again.');
    }

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Sync workspace from remote (git pull) with optional force_sync flag
      const response = await fetch(`${apiBaseUrl}/workspaces/${workspace.workspace_id}/sync`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ force_sync: forceSync })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to sync workspace: ${response.statusText}`);
      }

      const result = await response.json();

      // Refresh version status after successful sync
      if (result.success) {
        await checkWorkspaceVersion(workspace.workspace_id);
      }

      return result;
    } catch (error) {
      console.error('Error syncing workspace:', error);
      throw error;
    }
  };

  const handleClone = async (sourceWorkspace, newWorkspaceName) => {
    // sourceWorkspace is the workspace to clone from, newWorkspaceName is the name for the new workspace
    if (!sourceWorkspace?.workspace_id) {
      throw new Error('Source workspace ID is required.');
    }

    if (!newWorkspaceName?.trim()) {
      throw new Error('New workspace name is required.');
    }

    if (!clerk?.session) {
      throw new Error('Authentication required. Please sign in again.');
    }

    try {
      // Get Clerk session token for authentication
      const sessionToken = await clerk.session.getToken();
      if (!sessionToken) {
        throw new Error('Unable to get authentication token');
      }

      // Get organization-specific API URL
      const orgSlug = getOrgSlug(organization);
      const apiBaseUrl = getApiBaseUrl(orgSlug);

      // Clone the workspace
      const response = await fetch(`${apiBaseUrl}/workspaces/clone`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${sessionToken}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          source_workspace_id: sourceWorkspace.workspace_id,
          new_workspace_name: newWorkspaceName
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Failed to clone workspace: ${response.statusText}`);
      }

      const result = await response.json();

      // Store the cloned workspace info for switching when the modal closes
      if (result.workspace_id) {
        result._newWorkspace = {
          workspace_id: result.workspace_id,
          workspace_name: result.workspace_name,
          created_at: result.created_at,
          updated_at: result.updated_at,
          status: result.status,
          is_shared: result.is_shared || false,
          owner_user_id: result.owner_user_id,
          owner_name: result.owner_name || '',
          is_owner: true
        };
      }

      return result;
    } catch (error) {
      console.error('Error cloning workspace:', error);
      throw error;
    }
  };

  const handleClearSession = () => {
    if (chatInterfaceRef.current) {
      chatInterfaceRef.current.sendClearCommand();
      chatInterfaceRef.current.clearMessages();
    }
  };

  const handleCompactSession = () => {
    if (chatInterfaceRef.current) {
      chatInterfaceRef.current.sendCompactCommand();
    }
  };

  const handleOpenReportEditor = (reportId, reportName) => {
    setReportEditorPage({ reportId, reportName });
  };

  const handleCloseReportEditor = () => {
    setReportEditorPage(null);
  };

  // Auto-restore last selected workspace from localStorage
  useEffect(() => {
    const restoreLastWorkspace = async () => {
      // Only attempt restore once, and only when ready
      if (workspaceRestoreAttempted || !isLoaded || !orgLoaded || !organization || !clerk?.session) {
        return;
      }

      const savedWorkspaceId = localStorage.getItem('lastSelectedWorkspaceId');
      if (!savedWorkspaceId) {
        setWorkspaceRestoreAttempted(true);
        return;
      }

      console.log('Attempting to restore last workspace:', savedWorkspaceId);

      try {
        const sessionToken = await clerk.session.getToken();
        if (!sessionToken) {
          setWorkspaceRestoreAttempted(true);
          return;
        }

        const orgSlug = getOrgSlug(organization);
        const apiBaseUrl = getApiBaseUrl(orgSlug);

        const response = await fetch(`${apiBaseUrl}/workspaces/${savedWorkspaceId}`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'application/json',
          },
        });

        if (response.ok) {
          const workspace = await response.json();
          // Only auto-restore workspaces the user owns — shared workspaces require cloning
          if (workspace.is_owner) {
            console.log('Successfully restored workspace:', workspace.workspace_name);
            setSelectedWorkspace(workspace);
          } else {
            console.log('Skipping restore of non-owned workspace:', workspace.workspace_name);
            localStorage.removeItem('lastSelectedWorkspaceId');
          }
        } else {
          // Workspace no longer accessible (deleted, unshared, etc.) - clear saved ID
          console.log('Could not restore workspace (status:', response.status, ') - clearing saved ID');
          localStorage.removeItem('lastSelectedWorkspaceId');
        }
      } catch (error) {
        console.error('Error restoring workspace:', error);
        localStorage.removeItem('lastSelectedWorkspaceId');
      } finally {
        setWorkspaceRestoreAttempted(true);
      }
    };

    restoreLastWorkspace();
  }, [isLoaded, orgLoaded, organization, clerk, workspaceRestoreAttempted]);


  // Handle workspace deletion
  const handleWorkspaceDeleted = async (workspaceId) => {
    console.log('Workspace deleted:', workspaceId);
    // If the deleted workspace was selected, clear selection
    if (selectedWorkspace?.workspace_id === workspaceId) {
      setSelectedWorkspace(null);
    }
    // Also clear from localStorage if it was the saved workspace
    const savedWorkspaceId = localStorage.getItem('lastSelectedWorkspaceId');
    if (savedWorkspaceId === workspaceId) {
      localStorage.removeItem('lastSelectedWorkspaceId');
    }
  };

  // Check workspace version on selection and set up periodic checks
  useEffect(() => {
    if (!selectedWorkspace?.workspace_id) {
      // Clear version status and interval when workspace is deselected
      setVersionStatus(null);
      if (versionCheckIntervalRef.current) {
        clearInterval(versionCheckIntervalRef.current);
        versionCheckIntervalRef.current = null;
      }
      return;
    }

    // Check version immediately when workspace is selected
    checkWorkspaceVersion(selectedWorkspace.workspace_id);

    // Set up periodic check every 30 seconds
    versionCheckIntervalRef.current = setInterval(() => {
      checkWorkspaceVersion(selectedWorkspace.workspace_id);
    }, 30000);

    return () => {
      if (versionCheckIntervalRef.current) {
        clearInterval(versionCheckIntervalRef.current);
        versionCheckIntervalRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedWorkspace?.workspace_id, user, organization, clerk]);

  // Hide header on scroll down, show on scroll up (mobile only)
  useEffect(() => {
    const isMobile = () => window.innerWidth <= 768;

    const handleScroll = (e) => {
      if (!isMobile()) {
        setHeaderHidden(false);
        return;
      }
      const el = e.target;
      const scrollTop = el.scrollTop;
      const delta = scrollTop - lastScrollTopRef.current;

      if (scrollTop <= 0) {
        setHeaderHidden(false);
      } else if (delta > 5) {
        setHeaderHidden(true);
      } else if (delta < -5) {
        setHeaderHidden(false);
      }
      lastScrollTopRef.current = scrollTop;
    };

    // Attach to .messages-container once it appears in the DOM
    const observer = new MutationObserver(() => {
      const container = document.querySelector('.messages-container');
      if (container && !container.dataset.scrollBound) {
        container.dataset.scrollBound = 'true';
        container.addEventListener('scroll', handleScroll, { passive: true });
      }
    });
    observer.observe(document.body, { childList: true, subtree: true });

    // Also try binding immediately
    const container = document.querySelector('.messages-container');
    if (container && !container.dataset.scrollBound) {
      container.dataset.scrollBound = 'true';
      container.addEventListener('scroll', handleScroll, { passive: true });
    }

    return () => {
      observer.disconnect();
      const el = document.querySelector('.messages-container');
      if (el) {
        el.removeEventListener('scroll', handleScroll);
        delete el.dataset.scrollBound;
      }
    };
  }, []);

  // Saved reports are now loaded from backend API in TemplatePanel component

  // Show loading state while Clerk is initializing
  if (!isLoaded || !orgLoaded) {
    return (
      <div className="App">
        <div className="main-content" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>Loading...</div>
            <div style={{ color: '#666' }}>Initializing authentication</div>
          </div>
        </div>
      </div>
    );
  }

  // If user is signed in but has no organization, show organization creation
  const needsOrganization = user && (!user.organizationMemberships || user.organizationMemberships.length === 0);


  return (
    <ThemeProvider>
    <div className="App">
      <Header
        user={user?.id}
        isConnected={isConnected}
        onLogout={handleLogout}
        headerHidden={headerHidden}

      />
      
      <main className="main-content">
        <SignedOut>
          {/* Show Clerk's sign-in component when user is not authenticated */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center', 
            minHeight: '80vh',
            padding: '20px'
          }}>
            <SignIn 
              routing="virtual"
              afterSignInUrl={window.location.origin}
              afterSignUpUrl={window.location.origin}
              appearance={{
                elements: {
                  rootBox: 'mx-auto',
                  card: 'shadow-lg'
                }
              }}
            />
          </div>
        </SignedOut>

        <SignedIn>
          {needsOrganization ? (
            /* User is signed in but needs to create/join an organization */
            <CreateOrganizationFlow />
          ) : (
            /* Show main app when user is authenticated AND has organization */
            <WorkspaceProvider>
            {reportEditorPage ? (
              /* Full-page Report Editor */
              <ReportEditorPage
                reportId={reportEditorPage.reportId}
                reportName={reportEditorPage.reportName}
                selectedWorkspace={selectedWorkspace}
                onClose={handleCloseReportEditor}
              />
            ) : (
              <>
              {!selectedWorkspace && (
                <WorkspaceLandingPage
                  onWorkspaceSelect={handleWorkspaceSelect}
                  onCloneSharedWorkspace={handleCloneSharedWorkspace}
                  onDeleteWorkspace={(ws) => setDeleteConfirmOpen(ws)}
                  refreshTrigger={workspaceListVersion}
                />
              )}
              <div className={`app-layout ${!selectedWorkspace ? 'no-workspace-selected' : ''}`}>
              <LeftPanel
                ref={leftPanelRef}
                savedReportsPanelRef={savedReportsPanelRef}
                savedReports={savedReports}
                setSavedReports={setSavedReports}
                onLoadReport={handleLoadReport}
                user={user?.id}
                selectedWorkspace={selectedWorkspace}
                onSaveReport={handleOpenSaveDialog}
                onSelectedFilesChange={setSelectedReferenceFiles}
                onOpenReportEditor={handleOpenReportEditor}
                reportProgressEvents={reportProgressEvents}
              />
              <div className="chat-section">
                <WorkspaceHeader
                  selectedWorkspace={selectedWorkspace}
                  onWorkspaceChange={handleTemplateChange}
                  isConnected={isConnected}
                  onClearSession={handleClearSession}
                  onCompactSession={handleCompactSession}
                />
                <ChatInterface
                  ref={chatInterfaceRef}
                  user={user?.id}
                  onConnectionChange={setIsConnected}
                  selectedWorkspace={selectedWorkspace}
                  onSaveReport={handleSaveReport}
                  currentModel={currentModel}
                  onModelChange={handleModelChange}
                  selectedReferenceFiles={selectedReferenceFiles}
                  onReportProgress={handleReportProgress}
                  onProcessingChange={handleProcessingChange}
                />
              </div>
              <WorkspaceOptionsPanel
                selectedWorkspace={selectedWorkspace}
                onSync={handleSyncWorkspace}
                onSave={handleSaveWorkspaceClick}
                isSaving={isSavingWorkspace}
                onInstructions={() => setInstructionsModalOpen(true)}
                onShare={() => setShareConfirmOpen(selectedWorkspace)}
                onLogs={() => setLogsModalOpen(true)}
                onFiles={() => setFilesModalOpen(true)}
                onToggleGlossary={handleToggleGlossary}
                showGlossary={showGlossaryModal}
                onConnectionClick={handleConnectionClick}
                onAddConnection={handleAddConnection}
                user={user?.id}
                currentModel={currentModel}
                onModelChange={handleModelChange}
                onExplore={handleExploreDataSources}
                onClone={handleCloneWorkspace}
                versionStatus={versionStatus}
                onApiKeys={() => setApiKeyModalOpen(true)}
              />
              </div>
              </>
            )}
            </WorkspaceProvider>
          )}
        </SignedIn>
      </main>

      {/* Workspace Selection Modal */}
      <WorkspaceSelectionModal
        isOpen={templateModalOpen}
        onClose={handleTemplateModalClose}
        onWorkspaceSelect={handleWorkspaceSelect}
        onCloneSharedWorkspace={handleCloneSharedWorkspace}
        selectedWorkspace={selectedWorkspace}
        onWorkspaceDeleted={handleWorkspaceDeleted}
      />

      {/* Clone Workspace Modal */}
      <CloneWorkspaceModal
        isOpen={cloneModalOpen}
        onClose={handleCloneModalClose}
        selectedWorkspace={cloneSourceWorkspace}
        onClone={handleClone}
      />

      {/* Workspace Sync Modal */}
      <SyncWorkspaceModal
        isOpen={syncModalOpen}
        onClose={handleSyncModalClose}
        selectedWorkspace={selectedWorkspace}
        onSync={handleSync}
      />

      {/* Glossary Modal */}
      <GlossaryModal
        isOpen={showGlossaryModal}
        onClose={() => setShowGlossaryModal(false)}
        user={user?.id}
        selectedWorkspace={selectedWorkspace}
      />

      {/* Explore Data Sources Modal */}
      <ExploreDataSourcesModal
        isOpen={exploreModalOpen}
        onClose={handleExploreModalClose}
        onSendCommand={handleSendExploreCommand}
        isProcessing={false}
        connection={exploreConnection}
      />

      {/* Add Connection Modal - Select connection type */}
      <AddConnectionModal
        isOpen={showAddConnectionModal}
        onClose={handleAddConnectionClose}
        onSelectType={handleConnectionTypeSelected}
      />

      {/* Generic Connection Editor (Add/Edit connections) */}
      {activeConnectionEdit && (
        <GenericConnectionDialog
          isOpen={true}
          onClose={handleConnectionEditorClose}
          user={user?.id}
          selectedWorkspace={selectedWorkspace}
          onConfigSave={handleConnectionEditorSave}
          connectionType={activeConnectionEdit.type}
          connectionId={activeConnectionEdit.id}
          existingConfig={activeConnectionEdit.config}
        />
      )}

      {/* Instructions Modal */}
      <WorkspaceInstructionsModal
        isOpen={instructionsModalOpen}
        onClose={handleInstructionsModalClose}
        selectedWorkspace={selectedWorkspace}
      />

      {/* Share Workspace Modal */}
      <ShareWorkspaceModal
        isOpen={!!shareConfirmOpen}
        onClose={handleShareModalClose}
        selectedWorkspace={shareConfirmOpen}
        onShare={handleShareWorkspace}
      />

      {/* Delete Workspace Dialog */}
      {deleteConfirmOpen && (
        <div className="workspace-delete-overlay" onClick={handleDeleteModalClose}>
          <div className="workspace-delete-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="workspace-delete-header">
              <AlertTriangle size={20} />
              <h3>Delete Workspace</h3>
            </div>
            <div className="workspace-delete-body">
              <p>
                Are you sure you want to delete this workspace? This action cannot be undone.
              </p>
              <div className="workspace-delete-info">
                <strong>Workspace:</strong>
                <p style={{ margin: '0.5rem 0 0 0', fontSize: '0.9rem', color: '#666' }}>
                  {deleteConfirmOpen.workspace_name}
                </p>
              </div>
              <p className="workspace-delete-warning">
                All conversations, data, and configurations associated with this workspace will be permanently deleted.
              </p>
            </div>
            <div className="workspace-delete-footer">
              <button
                className="workspace-delete-cancel"
                onClick={handleDeleteModalClose}
              >
                Cancel
              </button>
              <button
                className="workspace-delete-confirm"
                onClick={handleDeleteWorkspace}
              >
                Delete Workspace
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs Modal */}
      <LogsModal
        isOpen={logsModalOpen}
        onClose={handleLogsModalClose}
        user={user?.id}
        selectedWorkspace={selectedWorkspace}
      />

      {/* Workspace Files Modal */}
      <WorkspaceFilesModal
        isOpen={filesModalOpen}
        onClose={() => setFilesModalOpen(false)}
        selectedWorkspace={selectedWorkspace}
      />

      {/* API Key Management Modal */}
      <ApiKeyModal
        isOpen={apiKeyModalOpen}
        onClose={() => setApiKeyModalOpen(false)}
        user={user?.id}
        selectedWorkspace={selectedWorkspace}
      />

    </div>
    </ThemeProvider>
  );
}

export default App;
